import React from 'react';

import {
  DiscountCodeDto,
  EventForUser,
  MultiTierCheckoutSession
} from '../../../types';
import { useGetMe } from 'legacy/user/hooks';
import useMultiTierCheckoutSession from './hooks/useMultiTierCheckoutSession';
import useSpecialCode from './hooks/useSpecialCode';
import useMultiTiers from './hooks/useMultiTiers';
import usePaymentMethod from 'hooks/usePaymentMethod';
import useWaivers from './hooks/useWaivers';
import { LoginResponse } from 'legacy/auth/types';
import useRequestScreening from './hooks/useRequestScreening';

export const DEFAULT_TICKET_QUANTITY = 1;

export enum StepsId {
  ConfirmTransactionStep = 'ConfirmTransaction',
  PrivateCodeStep = 'PrivateCodeForm',
  RequestScreeningStep = 'RequestScreening',
  SelectCardStep = 'SelectCardStep',
  SuccessRequestApprovalStep = 'SuccessRequestApprovalStep',
  SuccessStep = 'SuccessStep',
  TicketsSelectionStep = 'TicketsSelection',
  WaiversStep = 'WaiversStep'
}

export enum QuantityChangeEvents {
  Decrease = 'Decrease',
  Increase = 'Increase'
}

export type TicketMetadata = {
  quantity: number;
};

export type NormalizedTickets = Record<string, TicketMetadata>;

export type GoToStepFn = (stepId: StepsId) => void;

export type ToggleTicketTierFn = (
  toggledTicketId: string,
  callback?: (tickets: NormalizedTickets) => Promise<MultiTierCheckoutSession>
) => void;

export type UpdateQuantityFn = (
  ticketId: string,
  updateEvent: QuantityChangeEvents,
  callback?: (tickets: NormalizedTickets) => Promise<MultiTierCheckoutSession>
) => void;

export type StepProps = {
  goToStep: GoToStepFn;
};

export type MultiTierCheckoutProps = {
  defaultDiscountCode?: string;
  defaultPromoterCode?: string;
  event: EventForUser;
  unlockedTier?: { id: string; shareToken: string };
  onClose: () => void;
  onSuccess: () => void;
  onViewEventPostCheckout?: () => void;
  metaPixelRef?: React.MutableRefObject<any>;
};

export type MultiTierCheckoutProviderT = {
  data: {
    checkoutSession: ReturnType<typeof useMultiTierCheckoutSession>;
    appliedDiscount: {
      data: DiscountCodeDto;
      isLoading: boolean;
    };
    isLoading: boolean;
    paymentMethod: ReturnType<typeof usePaymentMethod>;
    requestScreening: ReturnType<typeof useRequestScreening>;
    specialCode: ReturnType<typeof useSpecialCode>;
    tickets: {
      maxTicketsPerTransaction: number;
      selectedTicketTiers: NormalizedTickets;
      toggleTicketTier: ToggleTicketTierFn;
      totalAmountOfTicketsSelected: number;
      updateQuantity: UpdateQuantityFn;
      tierRequiringApproval: string;
      toggleTierRequiringApproval: (tierId: string) => void;
    };
    ticketTiers: ReturnType<typeof useMultiTiers>;
    user: ReturnType<typeof useGetMe>['data'];
    waivers: ReturnType<typeof useWaivers>;
  } & Pick<MultiTierCheckoutProps, 'event'>;
  toggleTicketTierAndSave: (
    toggledTicketTier: string,
    unlockCode?: string
  ) => void;
  updateTicketTierQuantityAndSave: (
    ticketTier: string,
    event: QuantityChangeEvents
  ) => void;
  applyDiscountCodeAndSave: (code: string, discountCodeType?: string) => void;
  addNewCardAndSave: (options?: { onSuccess: () => void }) => void;
  onAuthenticationSuccess: (loginResponse: LoginResponse) => void;
  selectCardAndSave: (
    card: string,
    options?: { onSuccess: () => void }
  ) => void;
  selectBuyNowPayLaterAndSave: (
    buyNowPayLater: 'klarna' | 'affirm' | null,
    options?: { onSuccess: () => void }
  ) => void;
} & Pick<
  MultiTierCheckoutProps,
  'onClose' | 'onSuccess' | 'onViewEventPostCheckout'
>;
